import React, { ReactNode, createContext, useContext, useState } from 'react';

import { Context, States, Methods } from './types';
import { InitSurveyResponse } from '@/types/Psychosocial';

interface PsychosocialAvaliationProviderProps {
	children: ReactNode;
}

const PsychosocialAvaliationContext = createContext<Context>({} as Context);

export function PsychosocialAvaliationProvider({ children }: Readonly<PsychosocialAvaliationProviderProps>) {
	const [isModalFinishSurveyOpen, setIsModalFinishSurveyOpen] = useState<boolean>(false);
	const [isModalSaveAnswersOpen, setIsModalSaveAnswersOpen] = useState<boolean>(false);
	const [surveyData, setSurveyData] = useState<InitSurveyResponse | null>(null);
	const [isSurveyCompleted, setIsSurveyCompleted] = useState<boolean>(false);
	const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
	const [step, setStep] = useState<number>(0);

	let participationId = localStorage.getItem('participation_id');

	function handleToggleModalSaveAnswers(): void {
		setIsModalSaveAnswersOpen(!isModalSaveAnswersOpen);
	}

	function handleToggleModalFinishSurvey(): void {
		setIsModalFinishSurveyOpen(!isModalFinishSurveyOpen);
	}

	function nextStep(): void {
		setStep(step + 1);
	}

	function validateSurveyId(surveyId: string): string | null {
		if (surveyId && surveyId !== localStorage.getItem('survey_id')) {
			localStorage.removeItem('survey_id');
			localStorage.removeItem('participation_id');
			participationId = null;
		}

		return participationId;
	}

	function saveSurveyData(data: InitSurveyResponse): void {
		setSurveyData(data);

		if (data.participation.id) {
			setIsAuthenticated(true);
			localStorage.setItem('survey_id', data.participation.survey_id);
			localStorage.setItem('participation_id', data.participation.id);
		}
	}

	function completeSurveyStep(): void {
		setIsSurveyCompleted(true);
	}

	function clearParticipationId(): void {
		localStorage.removeItem('participation_id');
	}

	function resetSurvey(): void {
		setStep(0);
		setSurveyData(null);
		setIsSurveyCompleted(false);
		localStorage.removeItem('survey_id');
		localStorage.removeItem('participation_id');
	}

	const states: States = {
		isModalFinishSurveyOpen,
		isModalSaveAnswersOpen,
		isSurveyCompleted,
		participationId,
		isAuthenticated,
		surveyData,
		step
	};

	const methods: Methods = {
		handleToggleModalFinishSurvey,
		handleToggleModalSaveAnswers,
		clearParticipationId,
		completeSurveyStep,
		validateSurveyId,
		saveSurveyData,
		resetSurvey,
		nextStep
	};

	const context: Context = {
		...states,
		...methods
	};

	return <PsychosocialAvaliationContext.Provider value={context}>{children}</PsychosocialAvaliationContext.Provider>;
}

export function usePsychosocialAvaliationContext() {
	const context = useContext(PsychosocialAvaliationContext);
	return context;
}
